import { useEffect, useRef, useState } from 'react';
import { SingleImageBlock } from './SingleImageBlock';
import { CaretIcon, CloseIcon, GalleryIcon } from '../icons';
import { AnimatePresence, motion } from 'framer-motion';
import { FocusTrap } from 'focus-trap-react';

const CaretButton: React.FC<{ className?: string; onClick?: any }> = ({ className, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`hidden h-10 w-10 min-w-[40px] cursor-pointer items-center justify-center rounded-full outline outline-2 outline-offset-2 outline-transparent hover:outline-xumoBerry focus:outline-xumoBerry sm:flex md:h-12 md:w-12 md:min-w-[48px] md:bg-white md:hover:outline-xumoPlum md:focus:outline-xumoPlum ${className}`}
    >
      <div className="w-3">
        <CaretIcon className="fill-white md:fill-[#141417]" />
      </div>
    </button>
  );
};

export interface ImageGalleryBlockProps {
  image: any;
}

export function ImageGalleryBlock({ image }: ImageGalleryBlockProps) {
  const [activeImage, setActiveImage] = useState(0);
  const [galleryActive, setGalleryActive] = useState(false);
  const galleryLength = image.length;

  const setImageActive = (int: number) => {
    if (int >= 0 && int < galleryLength) {
      setActiveImage(int);
    } else if (int < 0) {
      setActiveImage(galleryLength - 1);
    } else {
      setActiveImage(0);
    }
  };

  //gallery animate in
  const galleryReveal = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { ease: [0, 1, 1, 1], duration: 0.8 },
    },
  };

  // prevent body scroll when gallery active
  useEffect(() => {
    if (galleryActive) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [galleryActive]);

  // scroll active image button into view
  const galleryThumbnailsRef = useRef<any>();

  useEffect(() => {
    if (!galleryActive) return;

    const container = galleryThumbnailsRef.current;
    const activeButton = document.getElementById(`image-btn-${activeImage}`);

    if (!container || !activeButton) return;

    const containerWidth = container.offsetWidth;
    const scrollWidth = container.scrollWidth;
    const buttonOffsetLeft = activeButton.offsetLeft;
    const buttonWidth = activeButton.offsetWidth;

    const target = buttonOffsetLeft - containerWidth / 2 + buttonWidth / 2;

    // Clamp so we don't overscroll
    const maxScroll = scrollWidth - containerWidth;
    const clamped = Math.max(0, Math.min(target, maxScroll));

    container.scrollTo({
      left: clamped,
      behavior: 'smooth',
    });
  }, [galleryActive, activeImage]);

  return (
    <>
      <motion.div
        className="group/gallery flex h-full w-full cursor-pointer flex-col items-end justify-start"
        onClick={() => setGalleryActive(true)}
        whileHover="hover"
        variants={galleryReveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.7 }}
      >
        <div className="absolute z-10 mr-2 mt-0.5 w-8 transition-all group-hover/gallery:scale-110">
          <GalleryIcon />
        </div>
        <SingleImageBlock image={image[activeImage].image} image_description={image[activeImage].image_description} hover />
      </motion.div>
      {/* Gallery */}
      <AnimatePresence>
        {galleryActive && (
          <FocusTrap>
            <motion.div
              className="fixed left-0 top-0 h-screen w-screen items-center justify-center bg-black/80 px-2 md:p-10"
              style={{ zIndex: 500, minHeight: '1px' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: galleryActive ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <motion.div
                className="flex h-full w-full flex-row items-center justify-center gap-4"
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: galleryActive ? 1 : 0, scale: galleryActive ? 1 : 0.3 }}
                exit={{ opacity: 0, scale: 0.3 }}
                transition={{ duration: 0.25, ease: 'easeOut', delay: 0.05 }}
                style={{ minHeight: '1px' }}
              >
                <CaretButton onClick={() => setImageActive(activeImage - 1)} />
                <div className="flex h-auto w-full max-w-[1250px] flex-col gap-4 overflow-hidden md:w-[75vw] md:bg-white">
                  <div className="flex h-full w-full items-start justify-center md:justify-end">
                    <button
                      className="absolute z-20 -mt-10 w-14 cursor-pointer rounded-full p-1 outline-offset-2 outline-transparent transition-all hover:scale-110 focus:outline-xumoBerry md:mr-3 md:mt-3 md:p-3 md:focus:outline-xumoPlum"
                      onClick={() => setGalleryActive(false)}
                    >
                      <div className="hidden md:block">
                        <CloseIcon />
                      </div>
                      <p className="block text-white md:hidden">Close</p>
                    </button>
                    <div className="grid h-full w-full grid-cols-1 grid-rows-1 items-center justify-center justify-items-center">
                      <AnimatePresence mode="sync">
                        <motion.div
                          className="col-start-1 row-start-1 h-full w-full"
                          key={`current-gallery-${activeImage}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.35, ease: 'easeOut' }}
                        >
                          <SingleImageBlock
                            image={image[activeImage].image}
                            image_description={image[activeImage].image_description}
                            border_radius="rounded-none"
                            no_motion
                            force_aspect="16/9"
                            image_class="object-contain object-center w-min md:rounded-b overflow-hidden mx-auto"
                            caption_class="md:px-4 text-white md:text-xumoDarkPlum"
                          />
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>
                  <div
                    ref={galleryThumbnailsRef}
                    className="flex w-full flex-row items-start justify-center gap-3 p-5 md:justify-start md:gap-4 md:overflow-x-scroll md:bg-[#FBFBFB]"
                  >
                    {image.map((item: any, id: number) => (
                      <button
                        id={`image-btn-${id}`}
                        onClick={() => setActiveImage(id)}
                        className={`flex h-2 w-2 cursor-pointer items-center justify-center overflow-hidden rounded-full ${
                          activeImage === id ? 'bg-white' : 'bg-white/70'
                        } outline outline-2 outline-offset-2 hover:outline-xumoPlum focus:outline-xumoBerry  md:aspect-video md:h-auto md:min-w-[150px] md:rounded md:bg-transparent md:focus:outline-xumoPlum ${
                          id === activeImage ? 'outline-white md:outline-black' : 'outline-transparent'
                        }`}
                      >
                        <div className="hidden h-full w-full md:block">
                          <SingleImageBlock border_radius="rounded-none" image={item.image} key={`img-${id}`} no_motion />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <CaretButton onClick={() => setImageActive(activeImage + 1)} className="rotate-180" />
              </motion.div>
            </motion.div>
          </FocusTrap>
        )}
      </AnimatePresence>
    </>
  );
}
